import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  HostListener,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationService } from './services/localization/localization.service';
import { ViewportScroller } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import * as AOS from 'aos';
import { environment } from 'src/environments/environment';
import { LoadingService } from './services/loading/loading.service';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthModalService } from './services/auth/auth-modal.service';
import { Subject } from 'rxjs';

declare let fbq: (...args: any[]) => void;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  title = 'up-career';
  isBrowser: boolean;
  loading = false;
  showNavbar = true; // Control navbar visibility
  showFooter = true;
  showAuthModal = false;
  currentLang: string = 'en';

  private destroy$ = new Subject<void>();

  // Company-related pages where footer should be visible for company users
  private companyFooterRoutes = [
    '/company-home',
    '/job-post',
    '/company-profile',
    '/job-list',
    '/job-description',
    '/search',
  ];
  isMobileView = false; // Track if device is mobile

  // Define routes where navbar should be hidden
  private hideNavbarRoutes = [
    '/company-admin',
    '/profile/personal',
    '/profile/auto-apply-jobs',
    '/profile/generated-cv',
    '/profile/evaluated-cvs',
    '/profile/generated-cover-letters',
    '/profile/evaluated-linkdin-profiles',
    '/profile/interview-training',
    '/profile/change-password',
  ];

  hideVrRoutes = 'virtual-interview-session';
  constructor(
    private translateService: TranslateService,
    private localizationService: LocalizationService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private loaderService: LoadingService,
    private authModalService: AuthModalService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentLang = this.localizationService.getUserLang();
    this.translateService.use(this.currentLang);

    this.localizationService.languageChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        this.currentLang = lang;
      });

    this.isBrowser = isPlatformBrowser(this.platformId);

    this.loaderService.loading$.subscribe((status: boolean) => {
      this.loading = status;
    });
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.loadFacebookPixel();
      AOS.init({ once: true });

      // Check initial screen size
      this.checkScreenSize();

      this.authModalService.show$
        .pipe(takeUntil(this.destroy$))
        .subscribe(show => this.showAuthModal = show);
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        if (this.isBrowser) {
          const path = event?.urlAfterRedirects;
          AOS.refresh();

          if (typeof fbq !== 'undefined') {
            fbq('track', 'PageView');
            fbq('trackCustom', 'RouteView', { path });
          }
        }

        // Check if navbar should be hidden on this route
        this.checkNavbarVisibility(event.urlAfterRedirects);
        this.checkFooterVisibility(event.urlAfterRedirects);
      });

    // Check initial route
    this.checkNavbarVisibility(this.router.url);
    this.checkFooterVisibility(this.router.url);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Listen to window resize events
  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    if (this.isBrowser) {
      this.checkScreenSize();
      // Re-check navbar visibility when screen size changes
      this.checkNavbarVisibility(this.router.url);
      this.checkFooterVisibility(this.router.url);
    }
  }

  private checkScreenSize(): void {
    if (this.isBrowser) {
      this.isMobileView = window.innerWidth <= 768;
    }
  }

  loadFacebookPixel() {
    if (this.isBrowser && typeof document !== 'undefined') {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1328108511805666');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
    }
  }

  private checkNavbarVisibility(url: string): void {
    const isAdminRoute = url.includes('/company-admin');
    const isVrSessionPage = url.includes(this.hideVrRoutes);
    const isServicePage = this.hideNavbarRoutes.some((route) =>
      url.includes(route)
    );

    // Always hide on company admin pages (they have their own sidebar)
    // Hide on mobile for profile service pages and VR session
    if (isAdminRoute || isVrSessionPage || (this.isMobileView && isServicePage)) {
      this.showNavbar = false;
    } else {
      this.showNavbar = true;
    }
  }
  private checkFooterVisibility(url: string): void {
    const isCompanyUser = this.isCompanyRole();

    // Always hide footer on company admin dashboard and profile pages
    const alwaysHideRoutes = ['/company-admin', '/virtual-interview-session'];
    if (alwaysHideRoutes.some((route) => url.includes(route))) {
      this.showFooter = false;
      return;
    }

    // Always show footer on company-facing public pages (including for guests)
    const companyPublicRoutes = ['/company-home', '/job-post'];
    if (companyPublicRoutes.some((route) => url.startsWith(route))) {
      this.showFooter = true;
      return;
    }

    if (isCompanyUser) {
      // For logged-in company users: hide footer on profile pages
      const hideOnRoutes = ['/profile/'];
      this.showFooter = !hideOnRoutes.some((route) => url.includes(route));
    } else {
      // For regular users and guests: always show footer
      this.showFooter = true;
    }
  }

  private isCompanyRole(): boolean {
    if (typeof localStorage === 'undefined') return false;

    // Check userInfo first (role may be an object like {name: 'COMPANY'})
    const userInfoStr = localStorage.getItem('userInfo');
    if (userInfoStr) {
      try {
        const parsed = JSON.parse(userInfoStr);
        const role = parsed?.role;
        const roleName =
          typeof role === 'object' && role !== null ? role.name : role;
        console.log('[Footer Debug] role from userInfo:', role, '| roleName:', roleName);
        if (roleName?.toString().toUpperCase() === 'COMPANY') return true;
      } catch {}
    }

    // Fallback: check 'role' key directly
    const roleRaw = localStorage.getItem('role');
    console.log('[Footer Debug] role from localStorage key:', roleRaw);
    if (roleRaw) {
      try {
        const parsed = JSON.parse(roleRaw);
        const roleName =
          typeof parsed === 'object' && parsed !== null ? parsed.name : parsed;
        return roleName?.toString().toUpperCase().includes('COMPANY') ?? false;
      } catch {
        return roleRaw.toString().toUpperCase().includes('COMPANY');
      }
    }

    return false;
  }

  closeAuthModal() {
    this.authModalService.close();
  }

  toggleLang() {
    const nextLang = this.currentLang === 'en' ? 'ar' : 'en';
    this.localizationService.setUserLang(nextLang);
  }

  goToAuth(type: string) {
    const redirectUrl = this.authModalService.redirectUrl;
    if (redirectUrl) {
      localStorage.setItem('redirectUrl', redirectUrl);
    }
    this.authModalService.close();
    // Determine if the user was on a company page based on redirectUrl or current route
    const currentPath = (this.router.url || '').split('?')[0];
    const contextUrl = redirectUrl || currentPath;
    const isCompanyContext = contextUrl.includes('company') || contextUrl.includes('job-post');
    const targetType = isCompanyContext ? 'company' : 'user';
    
    if (type === 'login') {
      this.router.navigate(['/login'], { queryParams: { type: targetType } });
    } else {
      this.router.navigate(['/sign-up'], { queryParams: { type: targetType } });
    }
  }
}
